/**
 * DataTable — generic, grid-based list/table.
 *
 * Two visual variants:
 *   `bare`     — no outer chrome, hairline rows, sticky header (Servis lists).
 *   `bordered` — outer 1 px line + vertical column dividers (Vozidla list).
 *
 * Backed by `@tanstack/react-table` so sorting / filtering / virtualization
 * can be added without changing the consumer-facing API. Layout is CSS grid
 * with per-column widths — TanStack handles the data model, the grid handles
 * the visual.
 *
 * Source:
 *   .agents/design/project/artboard-radical.jsx:178-218 (bare)
 *   .agents/design/project/artboard-radical-vozidla.jsx:44-160 (bordered)
 */

import {
  flexRender,
  tableFeatures,
  useTable,
  type ColumnDef,
  type RowData,
} from '@tanstack/react-table';
import { useMemo, type ReactNode } from 'react';
import { useBreakpoint } from '@/lib/useBreakpoint';

// No opt-in features yet (sorting/filtering/virtualization get registered here
// when a consumer needs them); the core row model is built in.
const features = tableFeatures({});

/** Constraint for a table's row type (react-table v9 `RowData`). */
export type DataTableRow = RowData;

export type RowTint = 'amber' | 'yellow' | 'mint' | 'accent';

const ROW_TINT_VARS: Record<RowTint, { bg: string; hover: string; active: string }> = {
  amber: {
    bg: 'var(--row-tint-amber)',
    hover: 'var(--row-tint-amber-hover)',
    active: 'var(--row-tint-amber-active)',
  },
  yellow: {
    bg: 'var(--row-tint-yellow)',
    hover: 'var(--row-tint-yellow-hover)',
    active: 'var(--row-tint-yellow-hover)',
  },
  mint: {
    bg: 'var(--row-tint-mint)',
    hover: 'var(--row-tint-mint-hover)',
    active: 'var(--row-tint-mint-active)',
  },
  accent: {
    bg: 'var(--accent-soft)',
    hover: 'var(--accent-soft)',
    active: 'var(--accent-soft)',
  },
};

export type ColumnAlign = 'left' | 'right' | 'center';

const ALIGN: Record<ColumnAlign, string> = {
  left: 'justify-start text-left',
  center: 'justify-center text-center',
  right: 'justify-end text-right',
};

/**
 * Card/compact mapping for the column (Phase 14.2). Tagging at least one
 * column opts the table into phone cards + tablet compact rows:
 *   `primary`   — the card's title line (15px semibold on phone)
 *   `secondary` — sub-line(s) under the title
 *   `meta`      — right-aligned stack (dates, counts, badges)
 * Untagged columns don't render below `lg` — desktop keeps every column.
 */
export type ColumnPriority = 'primary' | 'secondary' | 'meta';

export interface DataTableColumn<T> {
  id: string;
  header: ReactNode;
  cell: (row: T) => ReactNode;
  /** Grid track for this column. Default `1fr`. */
  width?: string;
  align?: ColumnAlign;
  /** Extra classes applied to every cell in this column (header + body). */
  cellClassName?: string;
  /** Card/compact placement below `lg` — see {@link ColumnPriority}. */
  priority?: ColumnPriority;
}

/**
 * A click or key that comes from a link, button or field inside a row belongs to that control, not to the row. Content a cell
 * renders through a portal (menu, popover) is not inside the row in the DOM, yet its events still bubble to the row in React's tree.
 */
const NESTED_CONTROL = 'a, button, input, select, textarea, label, [role="button"], [role="link"]';
function fromNestedControl(e: { target: EventTarget; currentTarget: EventTarget }): boolean {
  const row = e.currentTarget as Element;
  const target = e.target as Element | null;
  if (!target || !row.contains(target)) return true;
  const hit = target.closest?.(NESTED_CONTROL);
  return hit != null && hit !== row && row.contains(hit);
}

interface DataTableProps<T extends RowData> {
  columns: DataTableColumn<T>[];
  data: T[];
  variant?: 'bare' | 'bordered';
  /** Stable id for keys + selection. */
  getRowId: (row: T) => string;
  selectedId?: string;
  onRowClick?: (row: T) => void;
  /** Optional per-row tint (e.g. registered vs. stock). */
  getRowTint?: (row: T) => RowTint | undefined;
  /**
   * Optional full-width line rendered under a row's cells (spans all columns).
   * Return null/undefined to render nothing for that row.
   */
  renderRowDetail?: (row: T) => ReactNode;
  /** Rendered when `data.length === 0`. */
  empty?: ReactNode;
  /**
   * Full card override for phone/tablet (Phase 14.2) — wins over the
   * column-priority mapping. The returned node is the entire card body.
   */
  renderCard?: (row: T) => ReactNode;
  'aria-label'?: string;
  className?: string;
}

export function DataTable<T extends RowData>({
  columns,
  data,
  variant = 'bare',
  getRowId,
  selectedId,
  onRowClick,
  getRowTint,
  renderRowDetail,
  empty,
  renderCard,
  className = '',
  ...rest
}: DataTableProps<T>) {
  const tsColumns = useMemo<ColumnDef<typeof features, T>[]>(
    () =>
      columns.map((col) => ({
        id: col.id,
        header: () => col.header,
        cell: (ctx) => col.cell(ctx.row.original),
      })),
    [columns],
  );

  const table = useTable({
    features,
    columns: tsColumns,
    data,
    getRowId,
  });

  // Phase 14.2 — card (phone) / compact-row (tablet) modes. Opt-in per page:
  // tables whose columns carry no `priority` keep the desktop grid on every
  // breakpoint until their module's responsive phase tags them.
  const { isPhone, isTablet } = useBreakpoint();
  const hasCardSpec = renderCard != null || columns.some((c) => c.priority != null);
  const mode: 'table' | 'card' | 'compact' =
    hasCardSpec && isPhone ? 'card' : hasCardSpec && isTablet ? 'compact' : 'table';

  if (mode !== 'table') {
    return (
      <CardList
        mode={mode}
        columns={columns}
        rows={table.getRowModel().rows.map((r) => ({ id: r.id, original: r.original }))}
        isEmpty={data.length === 0}
        selectedId={selectedId}
        onRowClick={onRowClick}
        getRowTint={getRowTint}
        renderRowDetail={renderRowDetail}
        renderCard={renderCard}
        empty={empty}
        aria-label={rest['aria-label']}
        className={className}
      />
    );
  }

  const gridTemplate = columns.map((c) => c.width ?? '1fr').join(' ');
  const isBordered = variant === 'bordered';

  return (
    <div
      role="table"
      aria-label={rest['aria-label']}
      className={
        (isBordered ? 'flex min-h-0 flex-col border border-line bg-paper' : 'flex min-h-0 flex-col') +
        ' ' +
        className
      }
    >
      {/* Header — Phase 12.6 a11y: the ARIA spec requires `columnheader` to
          live under a `row` parent inside the `rowgroup`. Without the
          intermediate `<div role="row">` axe-core reports critical
          `aria-required-children` / `aria-required-parent` violations. */}
      <div
        role="rowgroup"
        className={
          'sticky top-0 z-10 font-mono text-[10px] font-semibold uppercase tracking-[0.08em] text-ink-3 ' +
          (isBordered
            ? 'border-b border-line bg-paper'
            : 'border-b border-line bg-bg')
        }
      >
        {table.getHeaderGroups().map((group) => (
          <div
            key={group.id}
            role="row"
            className="grid"
            style={{ gridTemplateColumns: gridTemplate }}
          >
            {group.headers.map((header, i) => {
              const col = columns[i];
              return (
                <div
                  key={header.id}
                  role="columnheader"
                  className={[
                    'flex items-center px-2.5 py-3',
                    isBordered && i < columns.length - 1 ? 'border-r border-line' : '',
                    ALIGN[col.align ?? 'left'],
                    col.cellClassName ?? '',
                  ].join(' ')}
                >
                  {flexRender(header.column.columnDef.header, header.getContext())}
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {/* Body — Phase 12.6 a11y: only attach `role="rowgroup"` when there
          are rows. An empty rowgroup fails `aria-required-children` because
          the empty-state placeholder is not a `role="row"`. Dropping the
          role keeps the markup valid without forcing the placeholder into
          the table semantics. */}
      <div
        role={data.length > 0 ? 'rowgroup' : undefined}
        className="flex-1 overflow-auto"
      >
        {data.length === 0 ? (
          empty ?? <DefaultEmpty />
        ) : (
          table.getRowModel().rows.map((row) => {
            const tint = getRowTint?.(row.original);
            const tintCss = tint ? ROW_TINT_VARS[tint] : null;
            const detail = renderRowDetail?.(row.original);
            const isSelected = row.id === selectedId;
            const interactive = !!onRowClick;
            return (
              <div
                key={row.id}
                role="row"
                data-row-tint={tint}
                aria-selected={isSelected || undefined}
                onClick={interactive ? (e) => !fromNestedControl(e) && onRowClick(row.original) : undefined}
                onKeyDown={
                  interactive
                    ? (e) => {
                        if (fromNestedControl(e)) return;
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          onRowClick(row.original);
                        }
                      }
                    : undefined
                }
                tabIndex={interactive ? 0 : undefined}
                style={{
                  gridTemplateColumns: gridTemplate,
                  background: tintCss
                    ? isSelected
                      ? tintCss.active
                      : tintCss.bg
                    : isSelected
                      ? 'var(--accent-soft)'
                      : undefined,
                  ['--row-hover-bg' as string]: tintCss
                    ? tintCss.hover
                    : isBordered
                      ? 'var(--bg)'
                      : 'var(--paper)',
                }}
                className={[
                  'group relative grid border-b border-line transition-colors',
                  interactive ? 'cursor-pointer' : '',
                  'hover:bg-[var(--row-hover-bg)]',
                  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent focus-visible:-outline-offset-2',
                ].join(' ')}
              >
                {columns.map((col, i) => {
                  return (
                    <div
                      key={col.id}
                      role="cell"
                      className={[
                        'flex items-center px-2.5 py-2.5 text-[12px] text-ink',
                        isBordered && i < columns.length - 1
                          ? 'border-r border-line'
                          : '',
                        ALIGN[col.align ?? 'left'],
                        col.cellClassName ?? '',
                      ].join(' ')}
                    >
                      {/*
                        Invoke the consumer's cell renderer directly rather than
                        via flexRender. flexRender wraps a function cell in
                        createElement(cellFn, ctx), so a new cell-function
                        identity each render (e.g. columns rebuilt inline) reads
                        as a new component type and React remounts the cell —
                        which resets focus/caret in editable cells (rename input
                        caret jumping to the end). Calling it inline keeps the
                        DOM node stable so the caret is preserved.
                      */}
                      {col.cell(row.original)}
                    </div>
                  );
                })}
                {detail != null && detail !== false && (
                  // `role="cell"` keeps the row's children valid for
                  // aria-required-children (same reason as the header fix
                  // above); col-span-full drops it onto a new implicit grid
                  // row below the cells.
                  <div role="cell" className="col-span-full px-2.5 pb-2.5">
                    {detail}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

function DefaultEmpty() {
  return (
    <div className="grid place-items-center px-8 py-16 font-mono text-[11px] uppercase tracking-[0.06em] text-ink-3">
      Žádné záznamy
    </div>
  );
}

interface CardListProps<T> {
  mode: 'card' | 'compact';
  columns: DataTableColumn<T>[];
  rows: { id: string; original: T }[];
  isEmpty: boolean;
  selectedId?: string;
  onRowClick?: (row: T) => void;
  getRowTint?: (row: T) => RowTint | undefined;
  renderRowDetail?: (row: T) => ReactNode;
  renderCard?: (row: T) => ReactNode;
  empty?: ReactNode;
  'aria-label'?: string;
  className?: string;
}

/**
 * Phone card / tablet compact-row rendering (Phase 14.2). Keeps the table
 * semantics — `role="table" > rowgroup > row > cell` — so a11y structure and
 * `getByRole('row')` selectors survive the layout change; each row holds a
 * single cell wrapping the card grid.
 *
 * Source: .agents/design/responsive/responsive-app.jsx `.kcard` (phone) /
 * `.trow` (tablet).
 */
function CardList<T>({
  mode,
  columns,
  rows,
  isEmpty,
  selectedId,
  onRowClick,
  getRowTint,
  renderRowDetail,
  renderCard,
  empty,
  className = '',
  ...rest
}: CardListProps<T>) {
  const primaries = columns.filter((c) => c.priority === 'primary');
  const secondaries = columns.filter((c) => c.priority === 'secondary');
  const metas = columns.filter((c) => c.priority === 'meta');
  const isCard = mode === 'card';

  return (
    // Unlike the desktop grid there is no header row in card/compact mode,
    // so an empty list would be a `role="table"` with zero `row` children —
    // a critical aria-required-children violation. Drop the table semantics
    // entirely when there is nothing to list.
    <div
      role={isEmpty ? undefined : 'table'}
      aria-label={isEmpty ? undefined : rest['aria-label']}
      className={
        (isCard ? 'flex min-h-0 flex-col bg-bg' : 'flex min-h-0 flex-col bg-paper') + ' ' + className
      }
    >
      <div
        role={isEmpty ? undefined : 'rowgroup'}
        className={'flex-1 overflow-y-auto ' + (isCard ? 'space-y-2 p-3' : '')}
      >
        {isEmpty
          ? (empty ?? <DefaultEmpty />)
          : rows.map(({ id, original }) => {
              const tint = getRowTint?.(original);
              const tintCss = tint ? ROW_TINT_VARS[tint] : null;
              const detail = renderRowDetail?.(original);
              const isSelected = id === selectedId;
              const interactive = !!onRowClick;
              return (
                <div
                  key={id}
                  role="row"
                  data-row-tint={tint}
                  aria-selected={isSelected || undefined}
                  onClick={interactive ? (e) => !fromNestedControl(e) && onRowClick(original) : undefined}
                  onKeyDown={
                    interactive
                      ? (e) => {
                          if (fromNestedControl(e)) return;
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            onRowClick(original);
                          }
                        }
                      : undefined
                  }
                  tabIndex={interactive ? 0 : undefined}
                  style={{
                    background: tintCss ? (isSelected ? tintCss.active : tintCss.bg) : undefined,
                  }}
                  className={[
                    'relative',
                    interactive ? 'cursor-pointer' : '',
                    isCard
                      ? 'border bg-paper p-3.5 active:bg-bg ' +
                        (isSelected ? 'border-accent' : 'border-line')
                      : 'border-b border-line px-5 py-3 hover:bg-bg ' +
                        (isSelected && !tintCss ? 'bg-accent-soft' : ''),
                    'focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent focus-visible:-outline-offset-2',
                  ].join(' ')}
                >
                  {isSelected && (
                    <span aria-hidden className="absolute inset-y-0 left-0 w-[3px] bg-accent" />
                  )}
                  <div role="cell">
                    {renderCard ? (
                      renderCard(original)
                    ) : (
                      <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 gap-y-1">
                        <div className="min-w-0">
                          <div
                            className={
                              'truncate font-semibold tracking-[-0.01em] text-ink ' +
                              (isCard ? 'text-[15px]' : 'text-[13.5px]')
                            }
                          >
                            {primaries.map((col) => (
                              <span key={col.id} className="[&+span]:before:content-['_·_']">
                                {col.cell(original)}
                              </span>
                            ))}
                          </div>
                          {secondaries.length > 0 && (
                            <div
                              className={
                                'mt-0.5 truncate ' +
                                (isCard ? 'text-[12.5px] text-ink-2' : 'text-[11.5px] text-ink-3')
                              }
                            >
                              {secondaries.map((col) => (
                                <span key={col.id} className="[&+span]:before:content-['_·_']">
                                  {col.cell(original)}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                        {metas.length > 0 && (
                          <div className="flex flex-col items-end gap-1 text-right text-[12px] text-ink-2">
                            {metas.map((col) => (
                              <div key={col.id}>{col.cell(original)}</div>
                            ))}
                          </div>
                        )}
                        {detail != null && detail !== false && (
                          <div className="col-span-full mt-1.5 border-t border-line pt-2.5">
                            {detail}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
      </div>
    </div>
  );
}
